'use client';
import { extractTableData } from '@/services/ecommerce/utils/ProductHelper';
import { useState } from 'react';
import ResponsiveTable from '../atoms/ResponsiveTable/ResponsiveTable';

type Props = {
	children: string;
	word?: number;
	className?: string;
	extraContent?: React.ReactNode;
	before?: boolean;
};

// Helper function to remove HTML tags but keep plain text
function removeHTMLTags(inputString: string): string {
	try {
		if (typeof document !== 'undefined') {
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = inputString;
			return tempDiv.textContent || tempDiv.innerText || '';
		}
		// Fallback for server-side environments
		return inputString.replace(/<[^>]*>/g, '');
	} catch (error) {
		console.error('Error in removeHTMLTags:', error);
		return inputString;
	}
}

// Helper function to remove only table elements
function removeHTMLTables(inputString: string): string {
	try {
		if (typeof document !== 'undefined') {
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = inputString;

			// Remove all <table> tags
			tempDiv.querySelectorAll('table').forEach((table) => table.remove());
			return tempDiv.innerHTML;
		}
		// Fallback for server-side environments
		return inputString.replace(/<table[^>]*>[\s\S]*?<\/table>/g, '');
	} catch (error) {
		console.error('Error in removeHTMLTables:', error);
		return inputString;
	}
}

// Helper function to truncate text by word limit
function truncateText(html: string, wordLimit: number): string {
	try {
		const plainText = removeHTMLTags(html);
		const wordsArray = plainText.split(/\s+/);

		if (wordsArray.length <= wordLimit) {
			// No truncation needed
			return plainText;
		}

		// Truncate plain text to word limit
		return wordsArray.slice(0, wordLimit).join(' ') + '...';
	} catch (error) {
		console.error('Error in truncateText:', error);
		return html;
	}
}

const Content = ({ children, word = 150, className, extraContent, before = false }: Props) => {
	const [isReadMore, setIsReadMore] = useState(true);

	const safeChildren = typeof children === 'string' ? children : '';

	// Content without tables (for initial display)
	const contentWithoutTables = removeHTMLTables(safeChildren);

	// Plain text without tables
	const plainTextWithoutTables = removeHTMLTags(contentWithoutTables);

	// Total words count based on content without tables
	const totalWordsWithoutTables = plainTextWithoutTables.split(/\s+/).length;

	// Check if tables were removed
	const tablesWereRemoved = contentWithoutTables !== safeChildren;

	// Displayed content
	const displayedContent = isReadMore
		? truncateText(contentWithoutTables, word) // Remove tables and truncate
		: safeChildren; // Full content (including tables)

	const toggleReadMore = () => {
		setIsReadMore(!isReadMore);
	};

	return (
		<div className={`text ${className}`}>
			{before && extraContent}

			{/* Render content */}
			{isReadMore && <div>{displayedContent}</div>}
			{!isReadMore && <DynamicContent itemContent={safeChildren} />}

			{/* Show "Show more/less" button */}
			{(tablesWereRemoved || totalWordsWithoutTables > word) && (
				<b
					onClick={toggleReadMore}
					style={{
						alignItems: 'center',
						fontWeight: 'bold',
						fontSize: '13px',
						color: 'black',
						marginTop: '20px',
						cursor: 'pointer'
					}}>
					{isReadMore ? '... Show more' : 'Show less'}
				</b>
			)}
			{!before && extraContent}
		</div>
	);
};

const ReadMore = ({ children, word, className, extraContent, before }: Props) => {
	const safeChildren = children || '';

	return (
		<div>
			<Content word={word} className={className} extraContent={extraContent} before={before}>
				{safeChildren}
			</Content>
		</div>
	);
};

export default ReadMore;

const DynamicContent = ({ itemContent }: { itemContent: string }) => {
	try {
		// Parse the content as HTML
		const parser = new DOMParser();
		const doc = parser.parseFromString(itemContent, 'text/html');

		// Split content into nodes
		const nodes = Array.from(doc.body.childNodes);

		// Render each node based on its type
		return (
			<div>
				{nodes.map((node: any, index) => {
					if (node.nodeType === Node.TEXT_NODE) {
						// Render text nodes
						return (
							<p key={index} className='mb-2'>
								{node.textContent?.trim()}
							</p>
						);
					} else if (node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'TABLE') {
						// Extract specific table data for this table
						const tableNode = node as HTMLTableElement | any;
						const extractedTableData = extractTableData(tableNode.outerHTML);

						// Use extracted table data to render the table
						return (
							<div className='mt-5' key={index}>
								<ResponsiveTable rows={extractedTableData.slice(1)} columns={extractedTableData[0]} />
							</div>
						);
					} else {
						// Render other elements (fallback for unexpected nodes)
						return (
							<div key={index} dangerouslySetInnerHTML={{ __html: node.outerHTML || '' }} className='mb-2' />
						);
					}
				})}
			</div>
		);
	} catch (error) {
		console.error('Error in DynamicContent:', error);
		return <div>Error rendering content</div>;
	}
};
